import { useConfig, useNotification, useOidc } from '@revenuewell/front-end-bundle';
import { useMemo, useState } from 'react';
import InsuranceV2Service from '../../services/insurance/insurance-service';
import { ApptRequestVerificationSummary } from '../../services/insurance/types/appt-request-verification-summary';
import CarrierSummary from '../../services/insurance/types/carrier-name';
import { KaynDashboardUrlResponse } from '../../services/insurance/types/kayn-dashboard-url-response';
import { PatientVerificationSummary } from '../../services/insurance/types/patient-verification-summary';
import { PracticeOnboarding } from '../../services/insurance/types/practice-onboarding';
import { Provider } from '../../services/insurance/types/provider';
import { VerificationPriority } from '../../services/insurance/types/verification-priority';
import { VerificationResponse } from '../../services/insurance/types/verification-response';
import { VerificationSummary } from '../../services/insurance/types/verification-summary';
import { ApiOperationStatus, InsuranceApiOperation } from './types';

export const useInsuranceApi = () => {
  const { config } = useConfig();
  const { oidcService } = useOidc();
  const { showError } = useNotification();

  const [status, setStatus] = useState<Record<InsuranceApiOperation, ApiOperationStatus>>({
    [InsuranceApiOperation.GetCarrierSummaries]: { isRunning: false, error: false },
    [InsuranceApiOperation.GetPracticeOnboarding]: { isRunning: false, error: false },
    [InsuranceApiOperation.GetProviders]: { isRunning: false, error: false },
    [InsuranceApiOperation.RunVerification]: { isRunning: false, error: false },
    [InsuranceApiOperation.SelfPayVerification]: { isRunning: false, error: false },
    [InsuranceApiOperation.SelfPayApptRequest]: { isRunning: false, error: false },
    [InsuranceApiOperation.ManualVerification]: { isRunning: false, error: false },
    [InsuranceApiOperation.ManuallyVerifyApptRequest]: { isRunning: false, error: false },
    [InsuranceApiOperation.GetVerificationDocumentUrl]: { isRunning: false, error: false },
    [InsuranceApiOperation.GetPatientVerificationSummaries]: { isRunning: false, error: false },
    [InsuranceApiOperation.GetAppointmentRequestVerificationSummaries]: { isRunning: false, error: false },
    [InsuranceApiOperation.GetAppointmentRequestVerificationSummary]: { isRunning: false, error: false },
    [InsuranceApiOperation.GetVerificationResponse]: { isRunning: false, error: false },
    [InsuranceApiOperation.GetVerificationSummary]: { isRunning: false, error: false },
    [InsuranceApiOperation.GetKaynDashboardUrl]: { isRunning: false, error: false }
  });

  const insuranceService = useMemo(() => new InsuranceV2Service(config, oidcService), [config, oidcService]);

  const updateStatus = (operation: InsuranceApiOperation, newStatus: Partial<ApiOperationStatus>) => {
    setStatus((st) => ({ ...st, [operation]: { ...st[operation], ...newStatus } }));
  };

  const getCarrierSummaries = async (locationId: number): Promise<CarrierSummary[]> => {
    try {
      updateStatus(InsuranceApiOperation.GetCarrierSummaries, { isRunning: true, error: false });

      return await insuranceService.getCarrierSummaries(locationId);
    } catch (e: any) {
      if (e.status !== 404) showError('Failed to fetch Carrier Summaries.');

      return [];
    } finally {
      updateStatus(InsuranceApiOperation.GetCarrierSummaries, { isRunning: false });
    }
  };

  const getPracticeOnboarding = async (locationId: number): Promise<PracticeOnboarding | null> => {
    try {
      updateStatus(InsuranceApiOperation.GetPracticeOnboarding, { isRunning: true, error: false });

      return await insuranceService.getPracticeOnboarding(locationId);
    } catch (e: any) {
      if (e.status !== 404) updateStatus(InsuranceApiOperation.GetPracticeOnboarding, { error: true });

      return null;
    } finally {
      updateStatus(InsuranceApiOperation.GetPracticeOnboarding, { isRunning: false });
    }
  };

  const getProviders = async (locationId: number): Promise<Provider[]> => {
    try {
      updateStatus(InsuranceApiOperation.GetProviders, { isRunning: true, error: false });

      return await insuranceService.getProviders(locationId);
    } catch (e: any) {
      return [];
    } finally {
      updateStatus(InsuranceApiOperation.GetProviders, { isRunning: false });
    }
  };

  const runVerification = async (
    locationId: number,
    idPatient: number,
    idProvider: number,
    verificationPriority: VerificationPriority,
    idCarrierName?: number
  ): Promise<boolean> => {
    try {
      updateStatus(InsuranceApiOperation.RunVerification, { isRunning: true, error: false });
      await insuranceService.runVerification(locationId, idPatient, idProvider, verificationPriority, idCarrierName);

      return true;
    } catch (e) {
      updateStatus(InsuranceApiOperation.RunVerification, { error: true });

      return false;
    } finally {
      updateStatus(InsuranceApiOperation.RunVerification, { isRunning: false });
    }
  };

  const selfPayVerification = async (
    locationId: number,
    idPatient: number,
    idProvider: number,
    verificationPriority: VerificationPriority
  ): Promise<boolean> => {
    try {
      updateStatus(InsuranceApiOperation.SelfPayVerification, { isRunning: true, error: false });
      await insuranceService.selfPayVerification(locationId, idPatient, idProvider, verificationPriority);

      return true;
    } catch (e) {
      updateStatus(InsuranceApiOperation.SelfPayVerification, { error: true });

      return false;
    } finally {
      updateStatus(InsuranceApiOperation.SelfPayVerification, { isRunning: false });
    }
  };

  const selfPayApptRequest = async (
    apptRequestId: number,
    locationId: number,
    providerId: number
  ): Promise<boolean> => {
    try {
      updateStatus(InsuranceApiOperation.SelfPayApptRequest, { isRunning: true, error: false });
      await insuranceService.selfPayApptRequest(apptRequestId, locationId, providerId);

      return true;
    } catch (e) {
      updateStatus(InsuranceApiOperation.SelfPayApptRequest, { error: true });

      return false;
    } finally {
      updateStatus(InsuranceApiOperation.SelfPayApptRequest, { isRunning: false });
    }
  };

  const manualVerification = async (
    locationId: number,
    idPatient: number,
    idProvider: number,
    verificationPriority: VerificationPriority
  ): Promise<boolean> => {
    try {
      updateStatus(InsuranceApiOperation.ManualVerification, { isRunning: true, error: false });
      await insuranceService.manualVerification(locationId, idPatient, idProvider, verificationPriority);

      return true;
    } catch (e) {
      updateStatus(InsuranceApiOperation.ManualVerification, { error: true });

      return false;
    } finally {
      updateStatus(InsuranceApiOperation.ManualVerification, { isRunning: false });
    }
  };

  const manuallyVerifyApptRequest = async (
    apptRequestId: number,
    locationId: number,
    idProvider: number
  ): Promise<boolean> => {
    try {
      updateStatus(InsuranceApiOperation.ManuallyVerifyApptRequest, { isRunning: true, error: false });

      await insuranceService.manuallyVerifyAppointmentRequest(apptRequestId, locationId, idProvider);
      return true;
    } catch (e) {
      updateStatus(InsuranceApiOperation.ManuallyVerifyApptRequest, { error: true });
      return false;
    } finally {
      updateStatus(InsuranceApiOperation.ManuallyVerifyApptRequest, { isRunning: false });
    }
  };

  const getVerificationDocumentUrl = async (
    locationId: number,
    verificationRequestId: number
  ): Promise<string | null> => {
    try {
      updateStatus(InsuranceApiOperation.GetVerificationDocumentUrl, { isRunning: true, error: false });

      return await insuranceService.getVerificationDocumentUrl(locationId, verificationRequestId);
    } catch (e) {
      showError('Unable to get verification document url.');

      return null;
    } finally {
      updateStatus(InsuranceApiOperation.GetVerificationDocumentUrl, { isRunning: false });
    }
  };

  const getPatientVerificationSummaries = async (
    patientIds: number[],
    locationId: number,
    providerId?: number
  ): Promise<PatientVerificationSummary[]> => {
    try {
      if (patientIds.length === 0) return [];

      updateStatus(InsuranceApiOperation.GetPatientVerificationSummaries, { isRunning: true, error: false });

      return await insuranceService.getPatientVerificationSummaries(patientIds, locationId, providerId);
    } catch (e: any) {
      if (e.status !== 404) showError('Unable to get patient verification summaries.');

      return [];
    } finally {
      updateStatus(InsuranceApiOperation.GetPatientVerificationSummaries, { isRunning: false });
    }
  };

  const getAppointmentRequestVerificationSummaries = async (
    appointmentRequetIds: number[],
    locationId: number
  ): Promise<ApptRequestVerificationSummary[]> => {
    try {
      if (appointmentRequetIds.length === 0) return [];

      updateStatus(InsuranceApiOperation.GetAppointmentRequestVerificationSummaries, { isRunning: true, error: false });

      const summaries = await insuranceService.getAppointmentRequestVerificationSummaries(
        appointmentRequetIds,
        locationId
      );

      appointmentRequetIds.forEach((id) => {
        if (!summaries.some((summary) => summary.appointmentRequestId === id)) {
          summaries.push({
            appointmentRequestId: id,
            locationId,
            verificationSummary: undefined
          });
        }
      });

      return summaries;
    } catch (e: any) {
      if (e.status !== 404) showError('Unable to get appointment request verification summaries.');

      return [];
    } finally {
      updateStatus(InsuranceApiOperation.GetAppointmentRequestVerificationSummaries, { isRunning: false });
    }
  };

  const getAppointmentRequestVerificationSummary = async (
    appointmentRequetId: number,
    locationId: number
  ): Promise<ApptRequestVerificationSummary | undefined> => {
    try {
      updateStatus(InsuranceApiOperation.GetAppointmentRequestVerificationSummary, { isRunning: true, error: false });

      return await insuranceService.getAppointmentRequestVerificationSummary(appointmentRequetId, locationId);
    } catch (e: any) {
      if (e.status !== 404) showError('Unable to get appointment request verification summary.');

      return undefined;
    } finally {
      updateStatus(InsuranceApiOperation.GetAppointmentRequestVerificationSummary, { isRunning: false });
    }
  };

  const getVerificationSummary = async (
    locationId: number,
    verificationRequestId: number
  ): Promise<VerificationSummary | null> => {
    try {
      updateStatus(InsuranceApiOperation.GetVerificationSummary, { isRunning: true, error: false });

      return await insuranceService.getVerificationSummary(locationId, verificationRequestId);
    } catch (e) {
      showError('Unable to get verification summary.');

      return null;
    } finally {
      updateStatus(InsuranceApiOperation.GetVerificationSummary, { isRunning: false });
    }
  };

  const getVerificationResponse = async (
    locationId: number,
    verificationRequestId: number
  ): Promise<VerificationResponse | null> => {
    try {
      updateStatus(InsuranceApiOperation.GetVerificationResponse, { isRunning: true, error: false });

      return await insuranceService.getVerificationResponse(locationId, verificationRequestId);
    } catch (e) {
      showError('Unable to get verification response.');

      return null;
    } finally {
      updateStatus(InsuranceApiOperation.GetVerificationResponse, { isRunning: false });
    }
  };

  const getKaynDashboardUrl = async (locationId: number): Promise<KaynDashboardUrlResponse | null> => {
    try {
      updateStatus(InsuranceApiOperation.GetKaynDashboardUrl, { isRunning: true, error: false });
      return await insuranceService.getKaynDahsboardUrl(locationId);
    } catch (e) {
      updateStatus(InsuranceApiOperation.GetKaynDashboardUrl, { isRunning: false, error: true });
      showError('Unable to get insurance dashboard url.');
      return null;
    } finally {
      updateStatus(InsuranceApiOperation.GetKaynDashboardUrl, { isRunning: false });
    }
  };

  return {
    status,
    getCarrierSummaries,
    getPracticeOnboarding,
    getProviders,
    runVerification,
    selfPayVerification,
    selfPayApptRequest,
    manualVerification,
    manuallyVerifyApptRequest,
    getVerificationDocumentUrl,
    getPatientVerificationSummaries,
    getAppointmentRequestVerificationSummaries,
    getAppointmentRequestVerificationSummary,
    getVerificationResponse,
    getVerificationSummary,
    getKaynDashboardUrl
  };
};
